// src/screens/s2/SessionProgress.tsx
// ─────────────────────────────────────────────────────────────────────────────
// Session 2 Screen 12 — Session Progress Summary.
// Three-column before-S1 / after-S1 / after-S2 comparison of freedom age,
// gap, gap-closed %, personal pension, business pension, CT saving. Visual
// horizontal timeline of the three freedom ages; gap bar reducing across
// the columns. Records computed s2FreedomAgeAfter / s2GapAfter back to
// ClientData on mount via a stabilised useEffect.
// ─────────────────────────────────────────────────────────────────────────────

import React, { useEffect, useMemo } from 'react';
import { ClientData } from '../../types/ClientData';
import { effectiveCTRate } from '../../taxConfig';
import { fvContributions } from '../../utils/planCalculations';
import { formatCurrency, formatAge, formatPercent } from '../../utils/formatters';

interface Props {
  data: ClientData;
  onUpdate: (updates: Partial<ClientData>) => void;
}

function Cell({ value, subtitle, accent }: { value: string; subtitle?: string; accent?: string }) {
  return (
    <div className="py-2">
      <div className="text-lg font-bold" style={{ color: accent || '#1B2B4B' }}>{value}</div>
      {subtitle && <div className="text-xs text-gray-500 mt-0.5">{subtitle}</div>}
    </div>
  );
}

export default function SessionProgress({ data, onUpdate }: Props) {
  // ── Freedom age lineage ────────────────────────────────────────────────
  const beforeS1 = data.s1FreedomAgeBefore ?? data.targetFreedomAge ?? 0;
  const afterS1 = data.targetFreedomAge ?? 0;
  const clientAge = data.clientAge || 40;

  // ── Gap lineage ─────────────────────────────────────────────────────────
  const gapBeforeS1 = data.s2GapBefore ?? data.gapAmount ?? 0;
  const gapAfterS1 = data.gapAmount ?? 0;

  // ── S2 contribution to gap closure ─────────────────────────────────────
  const s2PensionMonthly = data.s2PensionMonthly ?? 0;
  const yearsToFreedom = Math.max(0, afterS1 - clientAge);
  const growthRate = data.assumptionPensionGrowth || 0.08;
  const s2PensionPot = fvContributions(s2PensionMonthly, growthRate * 100, yearsToFreedom);
  const s2AnnualIncomeContribution = s2PensionPot * 0.04;
  const gapAfterS2Computed = Math.max(0, gapAfterS1 - s2AnnualIncomeContribution);

  const s1PensionMonthly = (data.pensionCompanyFunded || 0) + (data.planPensionFromDividendReduction || 0);

  // Persist computed S2 metrics (only when they've moved materially).
  useEffect(() => {
    const roundedGap = Math.round(gapAfterS2Computed);
    if (data.s2GapAfter !== roundedGap) onUpdate({ s2GapAfter: roundedGap });
    // Freedom age movement proxy: fraction of gap closed × years to freedom.
    const ageShift = gapBeforeS1 > 0 ? (s2AnnualIncomeContribution / gapBeforeS1) * yearsToFreedom : 0;
    const computedAge = Math.max(clientAge, Math.round(afterS1 - ageShift));
    if (data.s2FreedomAgeAfter !== computedAge) onUpdate({ s2FreedomAgeAfter: computedAge });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [gapAfterS2Computed, afterS1, s2AnnualIncomeContribution, gapBeforeS1, yearsToFreedom, clientAge]);

  const afterS2 = data.s2FreedomAgeAfter ?? Math.max(clientAge, afterS1);

  // ── CT saving across the sessions ──────────────────────────────────────
  const profit = data.estimatedCompanyProfit || 0;
  const ctRate = effectiveCTRate(profit);
  const s1CtSaved = (data.planPensionFromDividendReduction || 0) * 12 * ctRate;
  const s2CtSaved = s2PensionMonthly * 12 * ctRate;

  // ── Gap-closed percentages ─────────────────────────────────────────────
  const pctClosedS1 = gapBeforeS1 > 0 ? ((gapBeforeS1 - gapAfterS1) / gapBeforeS1) * 100 : 0;
  const pctClosedS2 = gapBeforeS1 > 0 ? ((gapBeforeS1 - gapAfterS2Computed) / gapBeforeS1) * 100 : 0;

  // ── Timeline positions (visual) ────────────────────────────────────────
  // Anchor endpoints at clientAge and (clientAge + 30) so the three markers
  // always sit in proportional positions across the scale.
  const timelineMin = clientAge;
  const timelineMax = Math.max(afterS1, beforeS1, afterS2, clientAge + 25) + 2;
  const pctFor = (age: number) => Math.max(0, Math.min(100, ((age - timelineMin) / (timelineMax - timelineMin)) * 100));

  // ── Gap bar widths ─────────────────────────────────────────────────────
  // Scale bar widths relative to the before-S1 anchor so the collapse is
  // visible even when the starting gap is small.
  const maxGap = Math.max(gapBeforeS1, 1);
  const gapBarWidth = (g: number) => `${(g / maxGap) * 100}%`;

  const progressRows = useMemo(() => [
    { label: 'Freedom age',    a: beforeS1 > 0 ? `Age ${formatAge(beforeS1)}` : '—',  b: afterS1 > 0 ? `Age ${formatAge(afterS1)}` : '—',  c: afterS2 > 0 ? `Age ${formatAge(afterS2)}` : '—' },
    { label: 'Annual gap',     a: gapBeforeS1 > 0 ? formatCurrency(gapBeforeS1) : '—', b: gapAfterS1 > 0 ? formatCurrency(gapAfterS1) : '£0', c: formatCurrency(gapAfterS2Computed) },
    { label: 'Gap closed',     a: '—',                                                 b: formatPercent(pctClosedS1),                       c: formatPercent(pctClosedS2) },
    { label: 'Monthly pension', a: '£0',                                                b: formatCurrency(s1PensionMonthly) + '/mo',         c: formatCurrency(s1PensionMonthly + s2PensionMonthly) + '/mo' },
    { label: 'Business pension', a: '£0',                                               b: formatCurrency((data.planPensionFromDividendReduction || 0)) + '/mo', c: formatCurrency(s2PensionMonthly) + '/mo (S2)' },
    { label: 'CT saving / yr',  a: '£0',                                                b: formatCurrency(s1CtSaved),                        c: formatCurrency(s1CtSaved + s2CtSaved) },
  ], [beforeS1, afterS1, afterS2, gapBeforeS1, gapAfterS1, gapAfterS2Computed, pctClosedS1, pctClosedS2, s1PensionMonthly, s2PensionMonthly, data.planPensionFromDividendReduction, s1CtSaved, s2CtSaved]);

  return (
    <div className="p-6 max-w-5xl mx-auto">
      <div className="mb-6">
        <h2 className="text-2xl font-bold text-ma-navy">Session progress</h2>
        <p className="text-gray-500 mt-1 text-sm">
          The full arc — before S1, after S1, after today.
        </p>
      </div>

      {/* TIMELINE */}
      <div className="bg-white rounded-2xl border border-gray-200 p-6 mb-6">
        <h3 className="text-sm font-bold text-ma-navy uppercase tracking-wide mb-4">Freedom age timeline</h3>
        <div className="relative h-16 mb-2">
          <div className="absolute top-1/2 left-0 right-0 h-1 bg-gray-200 rounded-full -translate-y-1/2" />
          {[
            { pct: pctFor(beforeS1), label: 'Before S1', age: beforeS1, colour: '#94A3B8' },
            { pct: pctFor(afterS1),  label: 'After S1',  age: afterS1,  colour: '#60A5FA' },
            { pct: pctFor(afterS2),  label: 'After S2',  age: afterS2,  colour: '#C9A84C' },
          ].map((m) => (
            <div
              key={m.label}
              className="absolute top-1/2 -translate-y-1/2 -translate-x-1/2 flex flex-col items-center"
              style={{ left: `${m.pct}%` }}
            >
              <div className="w-4 h-4 rounded-full" style={{ backgroundColor: m.colour, border: '2px solid white' }} />
              <div className="text-xs font-semibold text-gray-700 mt-1 whitespace-nowrap">{m.label}</div>
              <div className="text-[11px] text-gray-500 whitespace-nowrap">Age {formatAge(m.age)}</div>
            </div>
          ))}
        </div>
        <div className="flex justify-between text-xs text-gray-400 mt-4">
          <div>Age {timelineMin}</div>
          <div>Age {timelineMax}</div>
        </div>
      </div>

      {/* GAP BAR */}
      <div className="bg-white rounded-2xl border border-gray-200 p-6 mb-6">
        <h3 className="text-sm font-bold text-ma-navy uppercase tracking-wide mb-4">Annual gap reducing</h3>
        {/* Zero-state: when all three values are identical, three equal bars
            look broken. Show a single neutral message instead. Treat as
            equal if within £1 of each other to avoid float noise. */}
        {Math.abs(gapBeforeS1 - gapAfterS1) < 1 && Math.abs(gapAfterS1 - gapAfterS2Computed) < 1 ? (
          <p className="text-sm text-gray-500 italic">
            No levers applied yet — bars will show gap movement after S1 and S2 commitments are entered.
          </p>
        ) : (
        <div className="space-y-3">
          {[
            { label: 'Before S1', value: gapBeforeS1, colour: '#94A3B8' },
            { label: 'After S1',  value: gapAfterS1,  colour: '#60A5FA' },
            { label: 'After S2',  value: gapAfterS2Computed, colour: '#C9A84C' },
          ].map((g) => (
            <div key={g.label} className="grid grid-cols-6 gap-3 items-center">
              <div className="text-xs font-semibold text-gray-500">{g.label}</div>
              <div className="col-span-4 h-4 bg-gray-100 rounded-full overflow-hidden">
                <div className="h-full rounded-full" style={{ width: gapBarWidth(g.value), backgroundColor: g.colour }} />
              </div>
              <div className="text-right text-sm font-semibold text-ma-navy">{formatCurrency(g.value)}</div>
            </div>
          ))}
        </div>
        )}
      </div>

      {/* COMPARISON TABLE */}
      <div className="bg-white rounded-2xl border border-gray-200 p-6 mb-6">
        <h3 className="text-sm font-bold text-ma-navy uppercase tracking-wide mb-4">The numbers</h3>
        <div className="grid grid-cols-4 gap-4 pb-2 mb-1 border-b-2 border-gray-200 text-xs uppercase tracking-wide text-gray-400">
          <div>Metric</div>
          <div>Before S1</div>
          <div>After S1</div>
          <div>After S2</div>
        </div>
        {progressRows.map((row, i) => (
          <div key={i} className="grid grid-cols-4 gap-4 border-b border-gray-100 last:border-0 items-center">
            <div className="text-sm font-medium text-gray-600 py-2">{row.label}</div>
            <Cell value={row.a} />
            <Cell value={row.b} accent="#60A5FA" />
            <Cell value={row.c} accent="#C9A84C" />
          </div>
        ))}
      </div>
    </div>
  );
}
